import { format } from "date-fns";
import Head from "next/head";
import { useRouter } from "next/router";
import { Trans, useTranslation } from "next-i18next";
import React, { useState } from "react";
import styled, { css } from "styled-components";
import { ChevronLeftIcon } from "../../../../components/assets/icons/chevron-left.jsx";
import { BlogButton } from "../../../../components/blog/buttons/button.jsx";
import { Footer } from "../../../../components/blog/footerBlog/index.jsx";
import { Header } from "../../../../components/blog/header.jsx";
import { ShareAndContact } from "../../../../components/blog/ShareAndContact/index.jsx";
import { StaticI18nLink } from "../../../../components/Link.js";
import BlogPreviewPage from "../../../../components/Preview/index.jsx";
import WhatsAppButton from "../../../../components/WhatsAppButton/index.jsx";
import { device, size } from "../../../../constants.js";
import { useTheme } from "../../../../contexts/ThemeContext.js";
import { useWindowSize } from "../../../../hooks/useWindowResize.js";
import { getI18nProps } from "../../../../lib/next-i18next/getStatic.js";
import {
	getAllBlogSlug,
	getBlogPostsToGenerate,
	getPostAndMorePosts,
} from "../../../../services/CMSBlogService.js";
import { FaceLiftGlobalStyle } from "../../../../styles/theme.js";
import { calculateTimerPost } from "../../../../utils/blog/calculateTimerPost.js";
import { stripHtml } from "../../../../utils/stripHtml.js";

const BlogTitle = styled.h1`
  position: relative;

  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  color: ${({ theme }) => theme.colors["neutral-07"]};

  margin: 0;
  margin-top: 24px;
  padding-top: 16px;
  text-align: left;

  &::first-letter {
    text-transform: uppercase;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(0, -50%);
    width: 32px;
    height: 4px;
    background: ${({ theme }) => theme.colors["branding-01"]};
    border-radius: 100px;
  }

  @media ${device.tablet} {
    font-size: 32px;
    line-height: 120%;
    margin-top: 64px;
    padding-top: 24px;

    &::before {
      left: 0;
      transform: none;
      width: 48px;
    }
  }

  :is([data-theme='dark'] &) {
    color: ${({ theme }) => theme.colors["neutral-01"]};

    &::before {
      background: ${({ theme }) => theme.colors["branding-01"]};
    }
  }
`;

const PublicationDate = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;

  color: ${({ theme }) => theme.colors["neutral-04"]};
  margin-top: 8px;
  margin-bottom: 16px;
  text-align: left;

  @media ${device.tablet} {
    font-size: 16px;
    margin-bottom: 24px;
  }

  :is([data-theme='dark'] &) {
    color: ${({ theme }) => theme.colors["neutral-03"]};
  }
`;

const FirstImg = styled.img`
  border-radius: 8px;
  object-fit: cover;
  aspect-ratio: 2 / 1;
  width: 342px;
  height: 219px;

  @media ${device.tablet} {
    width: 100%;
    height: auto;
  }

  @media ${device.laptopL} {
    width: 1280px;
    height: 500px;
  }
`;

const FonteImg = styled.div`
  margin-top: 8px;

  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: ${({ theme }) => theme.colors["neutral-04"]};

  @media ${device.tablet} {
    font-size: 16px;
  }

  :is([data-theme='dark'] &) {
    color: ${({ theme }) => theme.colors["neutral-03"]};
  }
`;

const ContentText = styled.div`
  &.references-text {
    margin-top: 48px;

    p + p {
      margin: 0;
    }

    p {
      font-size: 18px;
    }
  }

  img {
    max-width: 100%;
  }

  * {
    color: ${({ theme }) => theme.colors["neutral-05"]};

    :is([data-theme='dark'] &) {
      color: ${({ theme }) => theme.colors["neutral-01"]};
    }
  }

  tbody, td {
    border-width: 1px;
  }

  & p {
    margin: 0;
    font-size: 18px;
    line-height: 32px;
    font-weight: 400;
    text-align: left;
    color: ${({ theme }) => theme.colors["neutral-05"]};

    :is([data-theme='dark'] &) {
      color: ${({ theme }) => theme.colors["neutral-01"]};
    }

    & + p {
      margin-top: 16px;

      @media ${device.tablet} {
        margin-top: 24px;
      }
    }
  }

  & strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors["neutral-05"]};

    :is([data-theme='dark'] &) {
      color: ${({ theme }) => theme.colors["neutral-01"]};
    }
  }

  & li {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 130%;
    text-align: left;
    color: ${({ theme }) => theme.colors["neutral-05"]};

    :is([data-theme='dark'] &) {
      color: ${({ theme }) => theme.colors["neutral-01"]};
    }

    & + li {
      margin-top: 1.5rem;
    }
  }

  & > :is(h1, h2, h3, h4, h5, h6) {
    font-weight: 700;
    font-size: 20px;
    line-height: 32px;
    color: ${({ theme }) => theme.colors["neutral-07"]};
    margin: 16px 0;

    &::first-letter {
      text-transform: uppercase;
    }

    :is([data-theme='dark'] &) {
      color: ${({ theme }) => theme.colors["neutral-01"]};
    }
  }

  @media ${device.tablet} {
    & > :is(h1, h2, h3, h4, h5, h6) {
      font-size: 26px;
      line-height: 48px;
      margin: 24px 0;
    }

    & li, & p {
      font-size: 22px;
      line-height: 40px;
      text-align: justify;
    }
  }
`;

const Hashtags = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  & strong {
    font-size: 18px;
    line-height: 150%;
    font-weight: 500;
    color: ${({ theme }) => theme.colors["branding-01"]};
  }

  :is([data-theme='dark'] &) {
    & strong {
      color: ${({ theme }) => theme.colors["vivid-01"]};
    }
  }

  @media ${device.tablet} {
    & strong {
      font-size: 20px;
      line-height: 150%;
    }
  }
`;

const ImageAndTextWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin: 16px 0;

  @media ${device.laptop} {
    gap: 32px;
    margin: 32px 0;
    grid-template-columns: minmax(516px, 1fr) 1fr;
  }
`;

const ImgText = styled.img`
  border-radius: 8px;
  width: 100%;
  height: 160px;
  border-radius: 8px;
  margin-top: 10px;
  object-fit: cover;

  @media ${device.tablet} {
    height: 260px;
  }

  @media ${device.laptop} {
    height: calc(100% - 24px);
  }
`;

const ImgContainer = styled.div`
`;

const GoBack = styled.a`
  font-size: 20px;
  line-height: 120%;
  font-weight: 500;
  color: ${({ theme }) => theme.colors["neutral-07"]};
  text-decoration: none;

  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: inherit;
  }

  :is([data-theme='dark'] &) {
    color: ${({ theme }) => theme.colors["neutral-01"]};
  }
`;

const BlogWrapper = styled.div`
  :is([data-theme='dark'] &) {
    background-color: ${({ theme }) => theme.colors["neutral-08"]};
  }
`;

const CustomContainer = styled.div`
  position: relative;
`;

const HiddenContent = styled.div`
  overflow: hidden;
  height: 200px;


  @media ${device.laptop} {
    height: 300px;
  }

  :after {
    content: '';
    position: absolute;
    height: 300px;
    bottom: 108px;
    right: 0;
    left: 0;
    width: 100%;
    backdrop-filter: blur(2px);

    @media ${device.laptop} {
      bottom: 128px;
    }
  }

  ${({ unhide }) =>
		unhide &&
		css`
    height: auto!important;

    :after {
      opacity: 0;
      visibility: hidden;
      display: none;
    }
  `}
`;

const ButtonShowMore = styled(BlogButton)`
  z-index: 2;
  width: 342px;
  height: 48px;
  margin: 0 auto;
  margin-top: 24px;
  font-weight: 600;
  font-size: 14px;
  line-height: 40px;
  letter-spacing: 1.4px;
  border-width: 1.5px;

  @media ${device.laptop} {
    width: 331px;
    margin-top: 40px;
    height: 56px;

    font-size: 18px;
    line-height: 40px;
    letter-spacing: 1.8px;
  }
`;

const PreviewWrapper = styled.div`
  padding: 24px;
  background-color: oklch(58.8% 0.158 241.966);
  text-align: center;

  p {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme.colors["neutral-01"]};

    span {
      color: ${({ theme }) => theme.colors["neutral-01"]};
      text-decoration: underline;
      font-weight: 600;
    }
  }
`;

const PostAuthor = styled.p`
  margin-top: 8px;
  margin-bottom: 16px;
  font-size: 18px;
  line-height: 26px;
  font-weight: 500;
  letter-spacing: 0%;
  color: ${({ theme }) => theme.colors["neutral-05"]};

  :is([data-theme='dark'] &) {
    color: ${({ theme }) => theme.colors["neutral-02"]};
  }

  @media ${device.laptop} {
    margin-bottom: 24px;
    line-height: 40px;
    font-size: 24px;
  }
`;

const BlogPostMetadata = ({ post }) => {
	const postLocale = post?.metadata?.linguagem_do_post?.key || "pt";
	const postUrl = `https://verzel.com.br/${postLocale}/blog/${post?.slug}/`;

	return (
		<Head>
			{/* <!-- Primary Meta Tags --> */}
			<title>{post?.title}</title>
			<meta name="title" content={post?.metadata?.titulo_pagina} />
			<meta name="description" content={post?.metadata?.meta_description} />

			{/* <!-- Open Graph / Facebook / Instagram --> */}
			<meta property="og:type" content="website" />
			<meta property="og:url" content={postUrl} />
			<meta property="og:title" content={post?.metadata?.titulo_pagina} />
			<meta
				property="og:description"
				content={post?.metadata?.meta_description}
			/>
			<meta
				property="og:image"
				content={post?.metadata?.imagem_com_texto.url}
			/>

			{/* <!-- LinkedIn --> */}
			<meta property="twitter:card" content="summary_large_image" />
			<meta property="twitter:url" content={postUrl} />
			<meta property="twitter:title" content={post?.metadata?.titulo_pagina} />
			<meta
				property="twitter:description"
				content={post?.metadata?.meta_description}
			/>
			<meta
				property="twitter:image"
				content={post?.metadata?.imagem_com_texto.url}
			/>
			{/* Make your website shareable on LinkedIn */}
		</Head>
	);
};

export function BlogPost({
	post,
	morePosts,
	mainContentWithoutHtml,
	preview = false,
}) {
	const [hiddenContent, setHiddenContent] = useState(true);
	const { width } = useWindowSize();
	const { theme } = useTheme();
	const { t, i18n } = useTranslation();

	const isDarkTheme = theme === "dark";
	const isTablet = width >= size.tablet;

	const dateFormat = {
		pt: "dd/MM/yyyy 'às' HH:mm -",
		en: "MM/dd/yyyy 'at' HH:mm -",
	};

	const publishedMessage =
		!isTablet || !post?.created_at
			? t("blog.published")
			: format(new Date(post.created_at), dateFormat[i18n.language || "pt"]);

	function handleShowMore() {
		if (preview) return;
		setHiddenContent(false);

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append(
			"X-Api-Key",
			process.env.NEXT_PUBLIC_VERZEL_LAMBDA_FUNCTIONS_KEY,
		);

		fetch(
			`${process.env.NEXT_PUBLIC_VERZEL_LAMBDA_FUNCTIONS_HOST}/slug-counter`,
			{
				method: "POST",
				headers,
				body: JSON.stringify({
					slug: post?.slug,
				}),
			},
		)
			.then((response) => response.json())
			.then(() => {
				console.log(`${post?.slug} - post viewed`);
			})
			.catch(() => {
				console.error("Error on post viewed");
			});
	}

	return (
		<>
			<FaceLiftGlobalStyle />
			<BlogPostMetadata post={post} mainContent={mainContentWithoutHtml} />
			<Header showHero={false} />
			<WhatsAppButton />

			<BlogWrapper>
				{preview && (
					<PreviewWrapper>
						<p>
							<Trans
								i18nKey="blog.previewMode"
								components={{ span: <span /> }}
							/>
						</p>
					</PreviewWrapper>
				)}
				<CustomContainer className="container-blog">
					<StaticI18nLink href="/blog/">
						<GoBack>
							<ChevronLeftIcon color={isDarkTheme ? "#fff" : "#1D202A"} />
							{t("blog.back")}
						</GoBack>
					</StaticI18nLink>

					<BlogTitle>{post?.title}</BlogTitle>
					{!!post?.metadata?.autor_do_post && (
						<PostAuthor>{post.metadata.autor_do_post}</PostAuthor>
					)}

					<PublicationDate>
						{publishedMessage}{" "}
						{calculateTimerPost(post?.created_at, i18n.language)}
					</PublicationDate>

					<div className="mb-3">
						<FirstImg src={post?.metadata?.imagem_inicio?.url} alt="" />
						{post?.metadata?.fonte_imagem_inicio !== "-" &&
							post?.metadata?.fonte_imagem_inicio !== "" &&
							post?.metadata?.fonte_imagem_inicio !== null && (
								<FonteImg>{post?.metadata?.fonte_imagem_inicio}</FonteImg>
							)}
					</div>
					<ContentText
						dangerouslySetInnerHTML={createMarkup(post?.metadata?.texto_inicio)}
					/>

					<HiddenContent unhide={preview || hiddenContent === false}>
						<ContentText
							dangerouslySetInnerHTML={createMarkup(post?.metadata?.texto_fim)}
						/>
						<ImageAndTextWrapper>
							<ImgContainer>
								<ImgText
									hasText={
										post?.metadata?.texto_com_imagem !== "" &&
										post?.metadata?.texto_com_imagem !== null &&
										post?.metadata?.texto_com_imagem !== "-"
									}
									src={post?.metadata?.imagem_com_texto?.url}
									alt="Imagem com text"
								/>
								{post?.metadata?.fonte_imagem_com_texto !== "-" &&
									post?.metadata?.fonte_imagem_com_texto !== "" &&
									post?.metadata?.fonte_imagem_com_texto !== null && (
										<FonteImg>
											{post?.metadata?.fonte_imagem_com_texto}
										</FonteImg>
									)}
							</ImgContainer>
							<ContentText
								dangerouslySetInnerHTML={createMarkup(
									post?.metadata?.texto_com_imagem,
								)}
							/>
						</ImageAndTextWrapper>

						{!!post?.metadata?.fontes_e_referencias && (
							<ContentText
								className="references-text"
								dangerouslySetInnerHTML={createMarkup(
									post?.metadata?.fontes_e_referencias,
								)}
							/>
						)}

						<Hashtags>
							{post?.metadata?.hashtags.split(" ").map((tag) => (
								<strong key={tag}>{tag}</strong>
							))}
						</Hashtags>
					</HiddenContent>

					{hiddenContent && !preview && (
						<ButtonShowMore
							onClick={handleShowMore}
							variant="green"
							outline={isDarkTheme}
						>
							{t("blog.continueReading")}
						</ButtonShowMore>
					)}
				</CustomContainer>
			</BlogWrapper>

			{(hiddenContent === false || preview) && (
				<ShareAndContact relatedPosts={morePosts} />
			)}
			<Footer />
		</>
	);
}

function createMarkup(text) {
	text = text?.replace(
		/href./g,
		'target="_blank" rel="noopener noreferrer" href=',
	);
	return { __html: text };
}

export const getStaticProps = async (ctx) => {
	const locale = ctx.params?.locale || "pt";
	const i18nProps = await getI18nProps(ctx);
	const data = await getPostAndMorePosts(ctx.params.slug, locale);

	if (!data?.post) {
		return {
			notFound: true,
		};
	}

	const mainContentWithoutHtml = stripHtml(data.post.content);

	return {
		props: {
			...i18nProps,
			post: data.post,
			morePosts: data.morePosts,
			mainContentWithoutHtml,
		},
	};
};

export async function getStaticPaths() {
	const allPosts = await getBlogPostsToGenerate();
	const posts = allPosts.map((post) => ({
		params: post,
	}));

	return {
		paths: posts,
		fallback: false,
	};
}

export default function BlogContentWrapper(props) {
	const router = useRouter();
	const { preview } = router.query;

	if (preview) {
		return <BlogPreviewPage />;
	}

	return <BlogPost {...props} />;
}
